import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import '../styles/CreatePost.css';
import { useMentionHandler } from '../utils/mentionHooks';

// Components
import Cropper from 'react-easy-crop'; // eslint-disable-line no-unused-vars
import LocationPicker from '../components/LocationPicker'; // eslint-disable-line no-unused-vars
import RichTextEditor from '../components/RichTextEditor'; // eslint-disable-line no-unused-vars
import VideoPlayer from '../components/VideoPlayer'; // eslint-disable-line no-unused-vars

// Firebase
import { addDoc, collection, doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from '../firebase';

// Utils
import { extractHashtags } from '../utils/hashtagUtils';
import { getCroppedImg } from '../utils/imageProcessing';
import { isVideoFile, prepareVideoForUpload, generateOptimizedThumbnail } from '../utils/videoUtils';

// Define utilities that might be missing
const cleanHashtags = (hashtags) => {
  return Array.isArray(hashtags) ? [...new Set(hashtags.map(tag => tag.toLowerCase().trim()))] : [];
};

const updateHashtagCounts = async (_newHashtags, _oldHashtags, _postId) => {
  // Touch parameters to satisfy linters
  if (_newHashtags || _oldHashtags || _postId) {
    /* no-op */
  }
  // Implementation will be added later
  return true;
};

const createContentData = (type, id) => {
  return { type, id };
};

const retryOperation = async (operation) => {
  return await operation();
};

const updatePostHashtagsAndMentions = async (_postId, _oldPostData, _newPostData, _currentUser) => {
  // Touch parameters to satisfy linters
  if (_postId || _oldPostData || _newPostData || _currentUser) {
    /* no-op */
  }
  // Implementation will be added later
  return true;
};

const needsVideoConversion = (file) => {
  // Simple check based on file size
  return file.size > 5 * 1024 * 1024; // Convert files larger than 5MB
};

function CreatePost() {
  const { currentUser, userProfile } = useAuth();
  const { handleMentions } = useMentionHandler(currentUser);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: urlPostId } = useParams(); // Pour détecter /edit-post/:id
  
  // Mode édition - peut venir des params URL ou de la route
  const editValue = searchParams.get('edit');
  const isEditModeFromParams = editValue !== null && editValue !== 'false';
  const isEditModeFromUrl = !!urlPostId; // Si on a un ID dans l'URL /edit-post/:id
  const isEditMode = isEditModeFromParams || isEditModeFromUrl;
  const editPostId = editValue || searchParams.get('id') || urlPostId;
  
  // États de base
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ingredients, setIngredients] = useState('');
  const [methodText, setMethodText] = useState('');
  const [methodSteps, setMethodSteps] = useState([{
    id: Date.now(),
    text: '',
    showTextarea: false,
    imageSrc: null,
    crop: { x: 0, y: 0 },
    zoom: 1,
    croppedAreaPixels: null,
    croppedImage: null,
    imageStep: 'none'
  }]);
  const [location, setLocation] = useState(null);
  const [isLocationValidated, setIsLocationValidated] = useState(false);
  // const [showLocationPicker, setShowLocationPicker] = useState(false); // legacy, replaced by showLocationPopup
  
  // États pour le média
  const [step, setStep] = useState('select');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  
  // États pour les vidéos
  // removed unused videoCurrentTime state
  const [videoDuration, setVideoDuration] = useState(0);
  
  // États pour les popups
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  
  // États pour les ressources
  // Resources optional state (kept for future enhancements)
  // const [resources, setResources] = useState([]);
  // const [selectedResourceType, setSelectedResourceType] = useState('');
  
  // États pour l'upload
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  // Pro: Product creation (Boutique)
  const isPro = !!userProfile?.isPro;
  const shopEnabled = !!userProfile?.shopEnabled;
  // Pro: creation mode switch (post or product)
  // null means user hasn't chosen yet (for Pro we want a pre-choice screen)
  const [creationMode, setCreationMode] = useState(null);
  useEffect(() => {
    // Non-pro users create normal posts by default
    if (!isPro && creationMode === null) {
      setCreationMode('post');
    }
  }, [isPro, creationMode]);
  // product section toggle is driven by creationMode now
  const [productTitle, setProductTitle] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productLink, setProductLink] = useState('');
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState(null);
  const [productImageSrc, setProductImageSrc] = useState(null);
  const [productImageStep, setProductImageStep] = useState('none'); // none | crop | preview
  const [productCrop, setProductCrop] = useState({ x: 0, y: 0 });
  const [productZoom, setProductZoom] = useState(1);
  const [productCroppedAreaPixels, setProductCroppedAreaPixels] = useState(null);
  const [productUploading, setProductUploading] = useState(false);
  const [productUploadStatus, setProductUploadStatus] = useState('');

  // États pour contrôler l'affichage des sections
  const [showTitleSection, setShowTitleSection] = useState(false);
  const [showDescriptionSection, setShowDescriptionSection] = useState(false);
  const [showResourcesSection, setShowResourcesSection] = useState(false);

  // Chargement du post en mode édition
  useEffect(() => {
    
    const loadPostForEdit = async () => {
      if (!isEditMode || !editPostId || !currentUser) return;
      
      setIsLoading(true);
      
      try {
        const postDoc = await getDoc(doc(db, 'posts', editPostId));
        
        if (!postDoc.exists()) {
          console.error('❌ Post non trouvé:', editPostId);
          alert(`Le post ${editPostId} n'existe pas ou a été supprimé.`);
          navigate('/');
          return;
        }
        
        const postData = postDoc.data();
        
        // Vérifier que l'utilisateur est bien l'auteur
        if (postData.userId !== currentUser.uid) {
          console.error('🔧 Cet utilisateur n\'est pas autorisé à modifier ce post');
          alert('Vous n\'êtes pas autorisé à modifier ce post');
          navigate('/');
          return;
        }
        
        // Configuration des données de base
        setTitle(postData.title || '');
        setDescription(postData.description || '');
        setIngredients(postData.ingredients || '');
        setMethodText(postData.methodText || '');

        // Initialiser l'état des sections basé sur les données existantes
        setShowTitleSection(!!postData.title?.trim());
        setShowDescriptionSection(!!postData.description?.trim());
        setShowResourcesSection(!!postData.ingredients?.trim());

        // Charger les étapes de méthode si présentes
        if (postData.methodSteps && Array.isArray(postData.methodSteps) && postData.methodSteps.length > 0) {
          setMethodSteps(postData.methodSteps.map((step, index) => ({
            id: step.id || Date.now() + index + 1,
            text: step.text || '',
            imageSrc: step.imageUrl || null,
            crop: { x: 0, y: 0 },
            zoom: 1,
            croppedAreaPixels: null,
            croppedImage: step.imageUrl ? { url: step.imageUrl } : null,
            imageStep: step.imageUrl ? 'preview' : 'none',
            fileType: step.mediaType || 'image' // Récupérer le type de média depuis la base de données
          })));
        }
          
        // Gérer l'image/vidéo principale
        if (postData.imageUrl) {
          setImageSrc(postData.imageUrl);
          setCroppedImage({ url: postData.imageUrl });
          
          // Détecter et configurer le type de média
          if (postData.mediaType === 'video') {
            setFileType('video');
          } else {
            setFileType('image');
          }
          
          setStep('preview');
        } else {
          // Si pas de média principal, aller directement en mode preview pour éditer les textes
          setStep('preview');
        }

        // Gérer la localisation
        if (postData.location) {
          setLocation(postData.location);
          setIsLocationValidated(true);
          setIsLocationValidated(true);
        }
        
      } catch (error) {
        console.error('🔧 Erreur lors du chargement du post:', error);
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser) {
      loadPostForEdit();
    }
  }, [isEditMode, editPostId, currentUser, navigate]);
  
  // En mode édition, nous ne préchargeons pas la vidéo pour la durée
  // La durée sera obtenue par le composant VideoPlayer au lieu de le faire ici
  useEffect(() => {
    if (isEditMode && fileType === 'video') {
      // Nous définissons une valeur par défaut raisonnable pour la durée
      // La vraie durée sera mise à jour quand le VideoPlayer sera chargé
      if (!videoDuration) {
        setVideoDuration(30); // Valeur par défaut
      }
    }
  }, [isEditMode, fileType, videoDuration]);

  // Surveiller les changements de contenu pour ajuster l'affichage des sections
  useEffect(() => {
    if (!isEditMode) {
      // Ajuster automatiquement l'affichage basé sur le contenu
      if (title.trim() && !showTitleSection) {
        setShowTitleSection(true);
      }
      if (description.trim() && !showDescriptionSection) {
        setShowDescriptionSection(true);
      }
      if (ingredients.trim() && !showResourcesSection) {
        setShowResourcesSection(true);
      }
    }
  }, [title, description, ingredients, isEditMode, showTitleSection, showDescriptionSection, showResourcesSection]);

  const onFileSelect = useCallback(async (file) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setFileType('image');
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setStep('crop'); // Toujours aller au crop pour les images
      };
      reader.readAsDataURL(file);
    } else if (file && isVideoFile(file)) {
      setIsLoading(true);
      setUploadStatus('Préparation de la vidéo...');
      
      try {
        let processedVideo = file;
        
        // Vérifier si la vidéo nécessite une conversion
        if (needsVideoConversion(file)) {
          setUploadStatus('Optimisation de la vidéo en cours...');
          
          // Préparer la vidéo (conversion + miniature)
          const result = await prepareVideoForUpload(file, (progress, message) => {
            setUploadProgress(progress);
            setUploadStatus(message || `Optimisation: ${progress.toFixed(1)}%`);
          });
          
          processedVideo = result.video;
          
          setUploadStatus(`Vidéo optimisée (${result.compressionRatio}% plus légère)`);
        } else {
          // Générer juste la miniature pour les vidéos déjà optimisées
          setUploadStatus('Génération de la miniature...');
          await generateOptimizedThumbnail(file);
          setUploadStatus('Vidéo prête');
        }
        
        setSelectedFile(processedVideo);
        setFileType('video');
        
        const reader = new FileReader();
        reader.onload = async () => {
          setImageSrc(reader.result);
          setCroppedImage({ url: reader.result, blob: processedVideo });
          
          // Set video duration for the slider
          const video = document.createElement('video');
          video.src = reader.result;
          video.onloadedmetadata = () => {
            setVideoDuration(video.duration);
          };
          
          setStep('preview'); // Les vidéos vont directement à la preview
        };
        reader.readAsDataURL(processedVideo);
        
      } catch (error) {
        console.error('❌ Erreur traitement vidéo:', error);
        setUploadStatus(`Erreur: ${error.message}`);
        alert(`Erreur lors du traitement de la vidéo: ${error.message}`);
      } finally {
        setIsLoading(false);
        setUploadProgress(0);
      }
    } else {
      console.warn('⚠️ Type de fichier non supporté:', file?.type);
      alert('Type de fichier non supporté. Veuillez sélectionner une image ou une vidéo.');
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleProductImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        setProductImageSrc(reader.result);
        setProductImageStep('crop');
        setProductCrop({ x: 0, y: 0 });
        setProductZoom(1);
        setProductCroppedAreaPixels(null);
        setProductImageFile(null);
        setProductImagePreview(null);
      };
      reader.readAsDataURL(file);
    } else if (file) {
      alert('Image de produit invalide. Formats acceptés: JPG, PNG, GIF.');
    }
  };

  const onProductCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
    setProductCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleProductCropConfirm = async () => {
    if (!productImageSrc || !productCroppedAreaPixels) return;
    try {
      const croppedBlob = await getCroppedImg(productImageSrc, productCroppedAreaPixels);
      const url = URL.createObjectURL(croppedBlob);
      setProductImageFile(croppedBlob);
      setProductImagePreview(url);
      setProductImageStep('preview');
    } catch (err) {
      console.error('Erreur crop image produit:', err);
      alert('Erreur lors du recadrage de l\'image produit');
    }
  };

  const handleProductImageReset = () => {
    if (productImagePreview) URL.revokeObjectURL(productImagePreview);
    setProductImageFile(null);
    setProductImagePreview(null);
    setProductImageSrc(null);
    setProductImageStep('none');
    setProductCrop({ x: 0, y: 0 });
    setProductZoom(1);
    setProductCroppedAreaPixels(null);
  };

  const resetProductForm = () => {
    setProductTitle('');
    setProductDescription('');
    setProductPrice('');
    setProductLink('');
    setProductImageFile(null);
    setProductImagePreview(null);
    setProductImageSrc(null);
    setProductImageStep('none');
    setProductCrop({ x: 0, y: 0 });
    setProductZoom(1);
    setProductCroppedAreaPixels(null);
  };

  const handleCreateProduct = async () => {
    if (!currentUser) {
      alert('Vous devez être connecté.');
      return;
    }
    if (!isPro) {
      alert('La création d\'article est réservée aux profils Pro.');
      return;
    }
    if (!shopEnabled) {
      alert('Activez la Boutique dans votre profil pour publier un article.');
      return;
    }
    if (!productTitle.trim()) {
      alert('Veuillez ajouter un nom d\'article.');
      return;
    }
    if (!productImageFile) {
      alert('Veuillez ajouter une photo de l\'article.');
      return;
    }
    const priceNumber = parseFloat(String(productPrice).replace(',', '.'));
    if (Number.isNaN(priceNumber) || priceNumber < 0) {
      alert('Veuillez saisir un prix valide.');
      return;
    }
      
  setProductUploading(true);
  setProductUploadStatus('Upload de l\'image produit...');
    try {
      // Upload image to Storage
      const imgRef = ref(storage, `social-app/products/${currentUser.uid}/${Date.now()}_product.jpg`);
      await uploadBytes(imgRef, productImageFile);
      const productImageUrl = await getDownloadURL(imgRef);

      setProductUploadStatus('Création de l\'article...');
      const docData = {
        ownerId: currentUser.uid,
        title: productTitle.trim(),
        description: productDescription.trim(),
        price: priceNumber,
        link: productLink.trim(),
        imageUrl: productImageUrl,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await addDoc(collection(db, 'products'), docData);

      setProductUploadStatus('Article créé');
      alert('Article publié dans votre Boutique.');
      resetProductForm();
      const username = userProfile?.username || currentUser.email.split('@')[0];
      navigate(`/profile/${username}`);
    } catch (err) {
      console.error('Erreur création produit:', err);
      alert(`Erreur lors de la création de l'article: ${err.message || err}`);
    } finally {
      setProductUploading(false);
      setTimeout(() => setProductUploadStatus(''), 1500);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropConfirm = async () => {
    try {
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
      setCroppedImage({ blob: croppedImageBlob, url: croppedImageUrl });
      setStep('preview');
    } catch (error) {
      console.error('Erreur lors du crop:', error);
      alert('Erreur lors du recadrage de l\'image');
    }
  };

  const handleBackToCrop = () => {
    if (fileType === 'video' || selectedFile?.type?.startsWith('video/')) {
      setStep('select');
    } else {
      setStep('crop');
      if (croppedImage?.url) {
        URL.revokeObjectURL(croppedImage.url);
      }
      setCroppedImage(null);
    }
  };

  const handleReset = () => {
    setStep('select');
    setTitle('');
    setSelectedFile(null);
    setFileType(null); // Reset du type de fichier
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    if (croppedImage?.url) {
      URL.revokeObjectURL(croppedImage.url);
    }
    setCroppedImage(null);
    setDescription('');
    setIngredients('');
    setMethodText('');
    setMethodSteps([{
      id: Date.now(),
      text: '',
      showTextarea: false,
      imageSrc: null,
      crop: { x: 0, y: 0 },
      zoom: 1,
      croppedAreaPixels: null,
      croppedImage: null,
      imageStep: 'none'
    }]); // Reset avec une étape par défaut
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!croppedImage || !currentUser) {
      alert('Veuillez sélectionner une image ou vidéo et vous connecter');
      return;
    }

    if (!title.trim()) {
      alert('Veuillez ajouter un titre à votre publication');
      return;
    }

    // Vérifier la connectivité réseau
    if (!navigator.onLine) {
      alert('Aucune connexion internet détectée. Veuillez vérifier votre connexion.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus('Préparation de l\'upload...');

    try {
      let imageUrl = croppedImage;

      // Upload du média principal (image ou vidéo)
      if (croppedImage.blob) {
        setUploadStatus('Upload du média principal...');

        const mediaRef = ref(storage, `social-app/posts/${currentUser.uid}/${Date.now()}`);
        const uploadTask = uploadBytesResumable(mediaRef, croppedImage.blob);

        // Promesse pour suivre le progrès
        imageUrl = await new Promise((resolve, reject) => {
          uploadTask.on('state_changed', 
            (snapshot) => {
              // Calcul du progrès (25% pour le média principal)
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 25;
              setUploadProgress(progress);
            },
            (error) => {
              console.error('❌ Erreur upload média principal:', error);
              reject(error);
            },
            async () => {
              // Upload terminé
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadURL);
            }
          );
        });

      } else if (croppedImage.url) {
        // Utiliser l'URL existante si c'est un média déjà uploadé
        imageUrl = croppedImage.url;
      }

      // Préparation des étapes de méthode avec upload des images
      const validMethodSteps = methodSteps.filter(step => step.text.trim());
      let methodStepsData = null;
      
      if (validMethodSteps.length > 0) {
        setUploadStatus('Upload des médias des étapes...');
        setUploadProgress(30);
        
        // Upload des images/vidéos d'étapes
        const stepsWithImages = await Promise.all(
          validMethodSteps.map(async (step, index) => {
            let stepMediaUrl = step.imageSrc;
            let stepMediaType = step.fileType || 'image';
            
            // Mise à jour du progrès pour chaque étape (30% à 70%)
            const stepProgress = 30 + ((index + 1) / validMethodSteps.length) * 40;
            setUploadProgress(stepProgress);
            setUploadStatus(`Upload étape ${index + 1}/${validMethodSteps.length}...`);
            
            // Upload seulement si c'est un nouveau média
            if (step.fileType === 'image' && step.croppedImage?.blob) {
              const stepImageRef = ref(storage, `social-app/posts/${currentUser.uid}/${Date.now()}_step_${step.id}`);
              const stepSnapshot = await uploadBytes(stepImageRef, step.croppedImage.blob);
              stepMediaUrl = await getDownloadURL(stepSnapshot.ref);
            } else if (step.fileType === 'image' && step.croppedImage?.url) {
              // Utiliser l'URL existante si c'est une image déjà uploadée
              stepMediaUrl = step.croppedImage.url;
            } else if (step.fileType === 'video' && (step.croppedImage?.blob || step.selectedFile)) {
              const stepVideoRef = ref(storage, `social-app/posts/${currentUser.uid}/${Date.now()}_step_${step.id}_video`);
              const videoFile = step.croppedImage?.blob || step.selectedFile;
              const stepSnapshot = await uploadBytes(stepVideoRef, videoFile);
              stepMediaUrl = await getDownloadURL(stepSnapshot.ref);
            } else if (step.fileType === 'video' && step.croppedImage?.url) {
              // Utiliser l'URL existante si c'est une vidéo déjà uploadée
              stepMediaUrl = step.croppedImage.url;
            }
            
            return {
              id: step.id,
              text: step.text.trim(),
              imageUrl: stepMediaUrl, // Garder le nom imageUrl pour compatibilité
              mediaType: stepMediaType
            };
          })
        );
        methodStepsData = stepsWithImages;
      }

      const postData = {
        title: title.trim(),
        imageUrl,
        description: description.trim(),
        hashtags: cleanHashtags(extractHashtags(description.trim())),
        ingredients: ingredients.trim(),
        methodText: methodText.trim(),
        methodSteps: methodStepsData,
        location: location, // Géolocalisation optionnelle
        updatedAt: serverTimestamp(),
      };


      if (isEditMode && editPostId) {
        // Mode édition - mise à jour du post existant
        setUploadStatus('Mise à jour du post...');
        setUploadProgress(80);
        
        // Récupérer les données de l'ancien post pour comparer
        const oldPostDoc = await getDoc(doc(db, 'posts', editPostId));
        const oldPostData = oldPostDoc.exists() ? oldPostDoc.data() : {};
        
        // En mode édition, préserver le mediaType existant si on n'a pas uploadé de nouveau média
        if (!croppedImage.blob && oldPostData.mediaType) {
          postData.mediaType = oldPostData.mediaType;
        } else {
          // Nouveau média uploadé, déterminer le type
          postData.mediaType = fileType === 'video' || selectedFile?.type?.startsWith('video/') ? 'video' : 'image';
        }
        
        // En mode édition, on ne modifie pas les miniatures vidéo
        // On garde la miniature existante
        
        // Mettre à jour le post dans Firestore
        await retryOperation(() => updateDoc(doc(db, 'posts', editPostId), postData));
        
        setUploadProgress(90);
        setUploadStatus('Finalisation...');
        
        // Utiliser le nouveau service pour mettre à jour hashtags et mentions
        await updatePostHashtagsAndMentions(editPostId, oldPostData, postData, currentUser);
        
        setUploadProgress(100);
        setUploadStatus('Post mis à jour avec succès !');
        
        setTimeout(() => {
          alert('Post modifié avec succès !');
          navigate(`/user/${userProfile?.username || currentUser.email.split('@')[0]}/post/${editPostId}`);
        }, 500);
      } else {
        // Mode création - nouveau post
        setUploadStatus('Sauvegarde du post...');
        setUploadProgress(80);
        
        // En mode création, déterminer le mediaType
        postData.mediaType = fileType === 'video' || selectedFile?.type?.startsWith('video/') ? 'video' : 'image';
        
        const docRef = await retryOperation(() => addDoc(collection(db, 'posts'), {
          ...postData,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          createdAt: serverTimestamp(),
          likes: [],
          comments: [],
          username: userProfile?.username || currentUser.email.split('@')[0],
          authorName: userProfile?.username || currentUser.email.split('@')[0],
          userProfilePicture: userProfile?.profilePicture || currentUser.photoURL,
          profilePicture: userProfile?.profilePicture || currentUser.photoURL,
          photoURL: userProfile?.profilePicture || currentUser.photoURL
        }));
        
        // Traiter les mentions dans la description
        if (description.trim()) {
          const contentData = createContentData('post', docRef.id);
          await handleMentions(description.trim(), contentData);
        }
        
        setUploadProgress(95);
        setUploadStatus('Finalisation...');
        
        // Mettre à jour les compteurs de hashtags
        if (postData.hashtags.length > 0) {
          await updateHashtagCounts(postData.hashtags, [], docRef.id);
        }
        
        setUploadProgress(100);
        setUploadStatus('Post créé avec succès !');
        
        
        setTimeout(() => {
          alert('Post créé avec succès !');
          handleReset();
          const username = userProfile?.username || currentUser.email.split('@')[0];
          navigate(`/profile/${username}`);
        }, 500);
      }
    } catch (error) {
      console.error(`❌ Erreur lors de ${isEditMode ? 'la modification' : 'la création'} du post:`, error);
      
      setUploadStatus('Erreur lors de la publication');
      setUploadProgress(0);
      
      // Gestion spécifique des erreurs réseau
      if (error.code === 'unavailable' || error.message.includes('QUIC_PROTOCOL_ERROR')) {
        alert('Problème de connexion réseau. Veuillez réessayer dans quelques instants.');
      } else if (error.code === 'permission-denied') {
        alert('Permissions insuffisantes. Veuillez vous reconnecter.');
      } else {
        alert(`Erreur lors de ${isEditMode ? 'la modification' : 'la création'} du post: ${error.message}`);
      }
    } finally {
      setIsUploading(false);
      setTimeout(() => {
        setUploadProgress(0);
        setUploadStatus('');
      }, 2000);
    }
  };

  // Fonctions pour la gestion des étapes de méthode avec images
  const addMethodStep = () => {
    const newStep = {
      id: Date.now(),
      text: '',
      showTextarea: false,
      imageSrc: null,
      crop: { x: 0, y: 0 },
      zoom: 1,
      croppedAreaPixels: null,
      croppedImage: null,
      imageStep: 'none'
    };
    setMethodSteps([...methodSteps, newStep]);
  };

  const removeMethodStep = (stepId) => {
    const stepToRemove = methodSteps.find(step => step.id === stepId);
    if (stepToRemove?.croppedImage?.url) {
      URL.revokeObjectURL(stepToRemove.croppedImage.url);
    }
    setMethodSteps(methodSteps.filter(step => step.id !== stepId));
  };

  const updateMethodStep = (stepId, newText) => {
    setMethodSteps(methodSteps.map(step => 
      step.id === stepId ? { ...step, text: newText } : step
    ));
  };

  // Removed unused legacy location handlers

  const updateMethodStepImage = (stepId, updates) => {
    setMethodSteps(prevSteps => prevSteps.map(step => 
      step.id === stepId ? { ...step, ...updates } : step
    ));
  };

  const onStepFileSelect = useCallback((stepId, file) => {
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          setMethodSteps(prevSteps => prevSteps.map(step => 
            step.id === stepId ? { 
              ...step, 
              imageSrc: reader.result,
              imageStep: 'crop',
              crop: { x: 0, y: 0 },
              zoom: 1,
              croppedAreaPixels: null,
              croppedImage: null,
              fileType: 'image'
            } : step
          ));
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        const reader = new FileReader();
        reader.onload = () => {
          setMethodSteps(prevSteps => prevSteps.map(step => 
            step.id === stepId ? { 
              ...step, 
              imageSrc: reader.result,
              imageStep: 'video-preview',
              selectedFile: file,
              fileType: 'video'
            } : step
          ));
        };
        reader.readAsDataURL(file);
      }
    }
  }, []);

  const handleStepCropComplete = useCallback((stepId, croppedArea, croppedAreaPixels) => {
    setMethodSteps(prevSteps => prevSteps.map(step => 
      step.id === stepId ? { ...step, croppedAreaPixels } : step
    ));
  }, []);

  const handleStepCropConfirm = async (stepId) => {
    const step = methodSteps.find(s => s.id === stepId);
    if (!step) return;

    try {
      const croppedImageBlob = await getCroppedImg(step.imageSrc, step.croppedAreaPixels);
      const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
      setMethodSteps(prevSteps => prevSteps.map(step => 
        step.id === stepId ? { 
          ...step, 
          croppedImage: { blob: croppedImageBlob, url: croppedImageUrl },
          imageStep: 'preview'
        } : step
      ));
    } catch (error) {
      console.error('Erreur lors du crop de l\'image étape:', error);
      alert('Erreur lors du recadrage de l\'image');
    }
  };

  const handleStepVideoConfirm = (stepId) => {
    setMethodSteps(prevSteps => prevSteps.map(step => 
      step.id === stepId ? { 
        ...step, 
        imageStep: 'preview',
        // Pour les vidéos, on crée un objet croppedImage avec l'URL et le fichier
        croppedImage: { url: step.imageSrc, blob: step.selectedFile }
      } : step
    ));
  };

  const handleStepImageReset = (stepId) => {
    const step = methodSteps.find(s => s.id === stepId);
    if (step?.croppedImage?.url) {
      URL.revokeObjectURL(step.croppedImage.url);
    }
    setMethodSteps(prevSteps => prevSteps.map(step => 
      step.id === stepId ? {
        ...step,
        imageSrc: null,
        crop: { x: 0, y: 0 },
        zoom: 1,
        croppedAreaPixels: null,
        croppedImage: null,
        imageStep: 'none'
      } : step
    ));
  };

  const handleStepBackToCrop = (stepId) => {
    const step = methodSteps.find(s => s.id === stepId);
    if (step?.croppedImage?.url) {
      URL.revokeObjectURL(step.croppedImage.url);
    }
    setMethodSteps(prevSteps => prevSteps.map(step => 
      step.id === stepId ? { 
        ...step, 
        croppedImage: null,
        imageStep: step.fileType === 'video' ? 'video-preview' : 'crop'
      } : step
    ));
  };

  const removeMethodStepImage = (stepId) => {
    const step = methodSteps.find(s => s.id === stepId);
    if (step?.croppedImage?.url) {
      URL.revokeObjectURL(step.croppedImage.url);
    }
    setMethodSteps(prevSteps => prevSteps.map(step => 
      step.id === stepId ? {
        ...step,
        imageSrc: null,
        crop: { x: 0, y: 0 },
        zoom: 1,
        croppedAreaPixels: null,
        croppedImage: null,
        imageStep: 'none',
        selectedFile: null,
        fileType: null
      } : step
    ));
  };

  return (
    <div className="create-post-container create-post-fullscreen">
      {/* Header fixe en haut - style bottom menu */}
      <div className="create-post-header">
        <button 
          className="close-create-post-btn"
          onClick={() => navigate('/')}
          title="Fermer et retourner à l'accueil"
        >
          ✕
        </button>
  <h2>{isEditMode ? 'Modifier la publication' : 'Créer un nouveau post'}</h2>
  {!isUploading && creationMode && (
          creationMode === 'product' ? (
            <button
              type="button"
              disabled={productUploading || !shopEnabled || !productTitle.trim() || !productImageFile}
              className="header-publish-button"
              onClick={handleCreateProduct}
              title={shopEnabled ? 'Publier l\'article' : 'Activez la Boutique pour publier'}
            >
              PUBLIER
            </button>
          ) : (
            <button
              type="submit"
              disabled={!title.trim()}
              className="header-publish-button"
              onClick={handleSubmit}
              title={isEditMode ? 'Modifier le post' : 'Publier le post'}
            >
              PUBLIER
            </button>
          )
        )}
      </div>

      {isPro && creationMode !== null && (
        <div className="creation-mode-toggle" style={{padding: '8px 16px'}}>
          <div style={{display:'flex', alignItems:'center', gap:12, flexWrap:'wrap'}}>
            <span style={{fontWeight:600}}>Que souhaitez-vous créer ?</span>
            <div style={{display:'flex', gap:8}}>
              <button
                type="button"
                className={`btn-secondary ${creationMode==='post' ? 'active' : ''}`}
                onClick={() => { setCreationMode('post'); }}
              >
                Publication
              </button>
              <button
                type="button"
                className={`btn-secondary ${creationMode==='product' ? 'active' : ''}`}
                onClick={() => { setCreationMode('product'); }}
              >
                Article (Pro)
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="create-post-card" data-edit-mode={isEditMode}>
        <h2 className="create-post-title-inline">{isEditMode ? 'Modifier la publication' : 'Créer un nouveau post'}</h2>
        
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Chargement de la publication...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="create-post-form">
            {isPro && !isEditMode && creationMode === null && (
              <div className="creation-choice" style={{textAlign:'center', marginBottom:16}}>
                <h3 className="section-title-centered" style={{marginBottom:12}}>Choisir un type de création</h3>
                <div className="creation-choice-buttons" style={{display:'flex', gap:12, justifyContent:'center'}}>
                  <button type="button" className="btn-primary" onClick={() => setCreationMode('post')}>
                    Publication normale
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setCreationMode('product')}>
                    Article Boutique
                  </button>
                </div>
                <p className="creation-choice-hint" style={{marginTop:8}}>Vous pourrez changer de type à tout moment.</p>
              </div>
            )}
            {/* Mode Article (Pro): afficher le formulaire produit en premier */}
            {creationMode === 'product' && (
              <div className="product-form-card">
                <h3 className="section-title-centered">Nouvel article de Boutique</h3>
                {!shopEnabled && (
                  <div className="info-banner">
                    La Boutique est désactivée. Activez-la dans votre profil pour publier des articles.
                  </div>
                )}
                <div className="product-image-field">
                  <label className="field-label">Photo de l'article</label>
                  {productImageStep === 'none' && (
                    <input type="file" accept="image/*" onChange={handleProductImageChange} />
                  )}
                  {productImageStep === 'crop' && productImageSrc && (
                    <div className="crop-section">
                      <div className="cropper-container">
                        <Cropper
                          image={productImageSrc}
                          crop={productCrop}
                          zoom={productZoom}
                          aspect={1}
                          onCropChange={setProductCrop}
                          onCropComplete={onProductCropComplete}
                          onZoomChange={setProductZoom}
                        />
                      </div>
                      <div className="crop-controls">
                        <label>
                          Zoom: 
                          <input
                            type="range"
                            value={productZoom}
                            min={1}
                            max={3}
                            step={0.1}
                            onChange={(e) => setProductZoom(parseFloat(e.target.value))}
                          />
                        </label>
                      </div>
                      <div className="crop-buttons">
                        <button type="button" onClick={handleProductImageReset} className="btn-secondary">
                          Annuler
                        </button>
                        <button type="button" onClick={handleProductCropConfirm} className="btn-primary">
                          Confirmer le recadrage
                        </button>
                      </div>
                    </div>
                  )}
                  {productImageStep === 'preview' && productImagePreview && (
                    <div className="product-image-preview" style={{display:'flex', flexDirection:'column', alignItems:'center', gap:12}}>
                      <div className="square" style={{width:240, maxWidth:'100%', aspectRatio:'1 / 1', overflow:'hidden', borderRadius:'12px', border:'1px solid #e5e7eb'}}>
                        <img src={productImagePreview} alt="Aperçu article" style={{width:'100%', height:'100%', objectFit:'cover', display:'block'}} />
                      </div>
                      <button type="button" className="btn-secondary" onClick={handleProductImageReset}>Changer</button>
                    </div>
                  )}
                </div>
                <div className="product-fields">
                  <label className="field-label">Nom de l'article</label>
                  <input type="text" value={productTitle} onChange={(e) => setProductTitle(e.target.value)} maxLength={80} placeholder="Ex: Tirage Fine Art A4" className="title-input" />
                  <label className="field-label" style={{ marginTop: 12 }}>Description</label>
                  <textarea value={productDescription} onChange={(e) => setProductDescription(e.target.value)} rows="3" className="ingredients-textarea" placeholder="Décrivez l'article..." />
                  <div className="product-inline-fields" style={{display:'grid', gap:12, gridTemplateColumns:'1fr 1fr'}}>
                    <div>
                      <label className="field-label">Prix (€)</label>
                      <input type="number" step="0.01" min="0" value={productPrice} onChange={(e) => setProductPrice(e.target.value)} className="title-input" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="field-label">Lien (achat)</label>
                      <input type="url" value={productLink} onChange={(e) => setProductLink(e.target.value)} className="title-input" placeholder="https://votre-boutique.com/produit" />
                    </div>
                  </div>
                </div>
                <div className="product-actions" style={{display:'flex', gap:12, justifyContent:'flex-end', marginTop:16}}>
                  <button type="button" className="btn-secondary" onClick={() => { resetProductForm(); setCreationMode('post'); }}>Annuler</button>
                  <button type="button" className="btn-primary" disabled={productUploading || !shopEnabled || !productTitle.trim() || !productImageFile} onClick={handleCreateProduct}>
                    {productUploading ? (productUploadStatus || 'Publication...') : 'Publier l\'article'}
                  </button>
                </div>
              </div>
            )}

            {/* Mode Publication: flux standard */}
            {creationMode === 'post' && step === 'select' && (
              <div className="upload-section">
                {/* Section d'upload de photo principale en haut */}
                <div className="main-upload-section">
                  <div 
                    className="upload-area"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleFileChange}
                      className="file-input"
                      id="image-upload"
                    />
                    <label htmlFor="image-upload" className="upload-label">
                      <div className="upload-content">
                        <span className="upload-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 16.9A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M13 11L9 17h6l-4-6z" fill="currentColor"/>
                          </svg>
                        </span>
                        <p>Cliquez pour sélectionner une image ou glissez-déposez</p>
                        <p className="upload-hint">Formats acceptés: JPG, PNG, GIF, Vidéos</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Section titre avec pattern step-description */}
                <div className="title-section">
                  {showTitleSection || title.trim() ? (
                    <div className="title-section-display">
                      <input
                        type="text"
                        id="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Donnez un titre à votre publication..."
                        required
                        className="title-input"
                        maxLength={20}
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setShowTitleSection(true);
                        setTimeout(() => {
                          document.getElementById('title')?.focus();
                        }, 100);
                      }}
                      className="add-description-btn"
                    >
                      + Ajouter un titre
                    </button>
                  )}
                </div>

                {/* Section description avec pattern step-description */}
                <div className="description-section">
                  {showDescriptionSection || description.trim() ? (
                    <div className="description-section-display">
                      <RichTextEditor
                        value={description}
                        onChange={setDescription}
                        placeholder="Décrivez votre création... Utilisez @nom pour mentionner des utilisateurs et #hashtag pour les sujets"
                        maxLength={4000}
                        className="description-textarea" onMentionSelect={(user) => handleMentions(user)}
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setShowDescriptionSection(true);
                        setTimeout(() => {
                          document.querySelector('.description-textarea')?.focus();
                        }, 100);
                      }}
                      className="add-description-btn"
                    >
                      + Ajouter une description
                    </button>
                  )}
                </div>

                {/* Section méthode optionnelle */}
                <div className="method-section">
                  <h3 className="section-title-centered">Méthode</h3>
                  <div className="method-steps-container">
                    {methodSteps.length === 0 ? (
                      <button
                        type="button"
                        onClick={addMethodStep}
                        className="add-first-method-btn"
                      >
                        + Ajouter une méthode
                      </button>
                    ) : (
                      <>
                        {methodSteps.map((step, index) => (
                          <div key={step.id} className="method-step-editor">

                            {/* Upload d'image en premier */}
                            <div className="step-image-section">
                              {step.imageStep === 'none' && (
                                <div className="step-image-upload">
                                  <input
                                    type="file"
                                    accept="image/*,video/*"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) onStepFileSelect(step.id, file);
                                    }}
                                    className="file-input"
                                    id={`step-image-${step.id}`}
                                  />
                                  <label htmlFor={`step-image-${step.id}`} className="step-upload-label">
                                    <span className="upload-icon">
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M19 16.9A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M13 11L9 17h6l-4-6z" fill="currentColor"/>
                                      </svg>
                                    </span>
                                  </label>
                                </div>
                              )}

                              {step.imageStep === 'crop' && step.imageSrc && step.fileType === 'image' && (
                                <div className="step-crop-section">
                                  <div className="step-cropper-container">
                                    <Cropper
                                      image={step.imageSrc}
                                      crop={step.crop}
                                      zoom={step.zoom}
                                      aspect={1}
                                      onCropChange={(crop) => updateMethodStepImage(step.id, { crop })}
                                      onCropComplete={(croppedArea, croppedAreaPixels) => 
                                        handleStepCropComplete(step.id, croppedArea, croppedAreaPixels)
                                      }
                                      onZoomChange={(zoom) => updateMethodStepImage(step.id, { zoom })}
                                    />
                                  </div>
                                  <div className="step-crop-controls">
                                    <label>
                                      Zoom: 
                                      <input
                                        type="range"
                                        value={step.zoom}
                                        min={1}
                                        max={3}
                                        step={0.1}
                                        onChange={(e) => updateMethodStepImage(step.id, { zoom: parseFloat(e.target.value) })}
                                      />
                                    </label>
                                  </div>
                                  <div className="step-crop-buttons">
                                    <button 
                                      type="button" 
                                      onClick={() => removeMethodStepImage(step.id)}
                                      className="btn-secondary"
                                    >
                                      Supprimer
                                    </button>
                                    <button 
                                      type="button" 
                                      onClick={() => handleStepCropConfirm(step.id)}
                                      className="btn-primary"
                                    >
                                      Confirmer
                                    </button>
                                  </div>
                                </div>
                              )}

                              {step.imageStep === 'video-preview' && step.imageSrc && step.fileType === 'video' && (
                                <div className="step-video-section">
                                  <div className="step-video-container">
                                    <VideoPlayer
                                      src={step.imageSrc}
                                      autoPlay={false}
                                      muted={true}
                                      onDurationChange={(duration) => setVideoDuration(duration)}
                                    />
                                  </div>
                                  <div className="step-video-buttons">
                                    <button 
                                      type="button" 
                                      onClick={() => removeMethodStepImage(step.id)}
                                      className="btn-secondary"
                                    >
                                      Supprimer
                                    </button>
                                    <button 
                                      type="button" 
                                      onClick={() => handleStepVideoConfirm(step.id)}
                                      className="btn-primary"
                                    >
                                      Confirmer
                                    </button>
                                  </div>
                                </div>
                              )}

                              {step.imageStep === 'preview' && (step.croppedImage || (step.fileType === 'video' && step.imageSrc)) && (
                                <div className="step-image-preview">
                                  {step.fileType === 'video' ? (
                                    <VideoPlayer
                                      src={step.croppedImage?.url || step.imageSrc}
                                      autoPlay={false}
                                      muted={true}
                                      onDurationChange={(duration) => setVideoDuration(duration)}
                                    />
                                  ) : (
                                    <img 
                                      src={step.croppedImage.url} 
                                      alt={`Étape ${index + 1}`}
                                      className="step-preview-image"
                                    />
                                  )}
                                  <div className="step-image-actions">
                                    {!isEditMode && (
                                      <>
                                        <button 
                                          type="button" 
                                          onClick={() => handleStepBackToCrop(step.id)} 
                                          className="btn-secondary"
                                          title="Modifier l'image"
                                        >
                                          ✏️ Modifier
                                        </button>
                                        <button 
                                          type="button" 
                                          onClick={() => handleStepImageReset(step.id)} 
                                          className="btn-secondary"
                                          title="Supprimer l'image"
                                        >
                                          🗑️ Supprimer
                                        </button>
                                      </>
                                    )}
                                    {isEditMode && (
                                      <div className="edit-mode-notice-step">
                                        <span className="edit-mode-text">
                                          📷 Médias non modifiables en mode édition
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Description - style PostCard */}
                            <div className="step-description-section">
                              {step.showTextarea || step.text.trim() ? (
                                <div className={`step-description-display step-rich-text step-editor-${step.id}`}>
                                  <RichTextEditor
                                    value={step.text}
                                    onChange={(val) => updateMethodStep(step.id, val)}
                                    placeholder={`Décrivez l'étape ${index + 1}... Utilisez @nom et #hashtag`}
                                    maxLength={1000}
                                    className="step-textarea"
                                    onMentionSelect={(user) => handleMentions(user)}
                                  />
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Révéler l'éditeur riche et le focaliser
                                    updateMethodStepImage(step.id, { showTextarea: true });
                                    setTimeout(() => {
                                      document.querySelector(`.step-editor-${step.id} .editor-content`)?.focus();
                                    }, 100);
                                  }}
                                  className="add-description-btn"
                                >
                                  + Ajouter une description
                                </button>
                              )}
                            </div>

                            {/* Bouton supprimer l'étape */}
                            {methodSteps.length > 1 && (
                              <div className="step-delete-section">
                                <button
                                  type="button"
                                  onClick={() => removeMethodStep(step.id)}
                                  className="btn-delete-step"
                                  title={`Supprimer l'étape ${index + 1}`}
                                >
                                  🗑️ Supprimer cette étape
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                    
                        <button
                          type="button"
                          onClick={addMethodStep}
                          className="add-step-btn"
                        >
                      + Ajouter une étape
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Section ressources avec pattern step-description */}
                <div className="resources-section">
                  {showResourcesSection || ingredients.trim() ? (
                    <div className="resources-section-display">
                      <textarea
                        id="ingredients"
                        value={ingredients}
                        onChange={(e) => setIngredients(e.target.value)}
                        placeholder="Listez les matériaux, outils, logiciels... (un par ligne)"
                        rows="4"
                        className="ingredients-textarea"
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setShowResourcesSection(true);
                        setTimeout(() => {
                          document.getElementById('ingredients')?.focus();
                        }, 100);
                      }}
                      className="add-description-btn"
                    >
                      + Ajouter des ressources
                    </button>
                  )}
                </div>

                {/* Section localisation optionnelle */}
                <div className="location-section">
                  {!location || !isLocationValidated ? (
                    <button
                      type="button"
                      onClick={() => setShowLocationPopup(true)}
                      className="add-first-method-btn"
                    >
                      + Ajouter une localisation
                    </button>
                  ) : (
                    <div className="option-added">
                      <div className="option-summary">
                        <span className="option-label">📍 Localisation</span>
                        <p className="option-preview">
                          {location.address || `${location.lat?.toFixed(4)}, ${location.lng?.toFixed(4)}`}
                        </p>
                        <span className="option-meta">
                          {location.address ? 'Adresse validée' : 'Coordonnées GPS'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowLocationPopup(true)}
                        className="edit-option-btn"
                      >
                        ✏️ Modifier
                      </button>
                    </div>
                  )}
                </div>

              </div>
            )}

            {creationMode === 'post' && step === 'crop' && imageSrc && fileType === 'image' && (
              <div className="crop-section">
                <div className="cropper-container">
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>
                <div className="crop-controls">
                  <label>
                    Zoom: 
                    <input
                      type="range"
                      value={zoom}
                      min={1}
                      max={3}
                      step={0.1}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                    />
                  </label>
                </div>
                <div className="crop-buttons">
                  <button type="button" onClick={() => setStep('select')} className="btn-secondary">
                    Retour
                  </button>
                  <button type="button" onClick={handleCropConfirm} className="btn-primary">
                    Confirmer le recadrage
                  </button>
                </div>
              </div>
            )}

            {creationMode === 'post' && step === 'video-preview' && imageSrc && fileType === 'video' && (
              <div className="video-preview-section">
                <div className="video-container">
                  <VideoPlayer
                    src={imageSrc}
                    autoPlay={false}
                    muted={true}
                    onDurationChange={(duration) => setVideoDuration(duration)}
                  />
                </div>
                <div className="video-buttons">
                  <button type="button" onClick={() => setStep('select')} className="btn-secondary">
                    Retour
                  </button>
                  <button type="button" onClick={() => setStep('preview')} className="btn-primary">
                    Continuer
                  </button>
                </div>
              </div>
            )}

            {creationMode === 'post' && step === 'preview' && (
              <div className="preview-section">
                {/* Aperçu de l'image/vidéo uploadée */}
                <div className="image-preview">
                  {(fileType === 'video' || selectedFile?.type?.startsWith('video/')) ? (
                    <>
                      <VideoPlayer
                        src={imageSrc}
                        autoPlay={false}
                        muted={true}
                        onDurationChange={(duration) => setVideoDuration(duration)}
                      />
                    </>
                  ) : (
                    <img 
                      src={croppedImage?.url || croppedImage} 
                      alt="Aperçu"
                      className="preview-image"
                    />
                  )}
                  {/* Bouton de modification uniquement en mode création ou pour les images */}
                  {!isEditMode && (
                    <button 
                      type="button" 
                      onClick={handleBackToCrop} 
                      className="edit-option-btn"
                    >
                      ✏️ Modifier l'image
                    </button>
                  )}
                  {isEditMode && (
                    <div className="edit-mode-notice">
                      <span className="edit-mode-text">
                        📷 Média non modifiable en mode édition
                      </span>
                    </div>
                  )}
                </div>

                {/* Section titre avec pattern step-description */}
                <div className="title-section">
                  {showTitleSection || title.trim() ? (
                    <div className="title-section-display">
                      <input
                        type="text"
                        id="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Donnez un titre à votre publication..."
                        required
                        className="title-input"
                        maxLength={20}
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setShowTitleSection(true);
                        setTimeout(() => {
                          document.getElementById('title')?.focus();
                        }, 100);
                      }}
                      className="add-description-btn"
                    >
                      + Ajouter un titre
                    </button>
                  )}
                </div>

                {/* Section description avec pattern step-description */}
                <div className="description-section">
                  {showDescriptionSection || description.trim() ? (
                    <div className="description-section-display">
                      <RichTextEditor
                        value={description}
                        onChange={setDescription}
                        placeholder="Décrivez votre création... Utilisez @nom pour mentionner des utilisateurs et #hashtag pour les sujets"
                        maxLength={4000}
                        className="description-textarea" onMentionSelect={(user) => handleMentions(user)}
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setShowDescriptionSection(true);
                        setTimeout(() => {
                          document.querySelector('.description-textarea')?.focus();
                        }, 100);
                      }}
                      className="add-description-btn"
                    >
                      + Ajouter une description
                    </button>
                  )}
                </div>

                <div className="method-section">
                  <h3 className="section-title-centered">Méthode</h3>
                  <div className="method-steps-container">
                    {methodSteps.length === 0 ? (
                      <button
                        type="button"
                        onClick={addMethodStep}
                        className="add-first-method-btn"
                      >
                        + Ajouter une méthode
                      </button>
                    ) : (
                      <>
                        {methodSteps.map((step, index) => (
                          <div key={step.id} className="method-step-editor">

                            {/* Upload d'image en premier */}
                            <div className="step-image-section">
                              {step.imageStep === 'none' && (
                                <div className="step-image-upload">
                                  <input
                                    type="file"
                                    accept="image/*,video/*"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) onStepFileSelect(step.id, file);
                                    }}
                                    className="file-input"
                                    id={`step-image-${step.id}`}
                                  />
                                  <label htmlFor={`step-image-${step.id}`} className="step-upload-label">
                                    <span className="upload-icon">
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M19 16.9A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M13 11L9 17h6l-4-6z" fill="currentColor"/>
                                      </svg>
                                    </span>
                                  </label>
                                </div>
                              )}

                              {step.imageStep === 'crop' && step.imageSrc && step.fileType === 'image' && (
                                <div className="step-crop-section">
                                  <div className="step-cropper-container">
                                    <Cropper
                                      image={step.imageSrc}
                                      crop={step.crop}
                                      zoom={step.zoom}
                                      aspect={1}
                                      onCropChange={(crop) => updateMethodStepImage(step.id, { crop })}
                                      onCropComplete={(croppedArea, croppedAreaPixels) => 
                                        handleStepCropComplete(step.id, croppedArea, croppedAreaPixels)
                                      }
                                      onZoomChange={(zoom) => updateMethodStepImage(step.id, { zoom })}
                                    />
                                  </div>
                                  <div className="step-crop-controls">
                                    <label>
                                      Zoom: 
                                      <input
                                        type="range"
                                        value={step.zoom}
                                        min={1}
                                        max={3}
                                        step={0.1}
                                        onChange={(e) => updateMethodStepImage(step.id, { zoom: parseFloat(e.target.value) })}
                                      />
                                    </label>
                                  </div>
                                  <div className="step-crop-buttons">
                                    <button 
                                      type="button" 
                                      onClick={() => removeMethodStepImage(step.id)}
                                      className="btn-secondary"
                                    >
                                      Supprimer
                                    </button>
                                    <button 
                                      type="button" 
                                      onClick={() => handleStepCropConfirm(step.id)}
                                      className="btn-primary"
                                    >
                                      Confirmer
                                    </button>
                                  </div>
                                </div>
                              )}

                              {step.imageStep === 'video-preview' && step.imageSrc && step.fileType === 'video' && (
                                <div className="step-video-section">
                                  <div className="step-video-container">
                                    <VideoPlayer
                                      src={step.imageSrc}
                                      autoPlay={false}
                                      muted={true}
                                      onDurationChange={(duration) => setVideoDuration(duration)}
                                    />
                                  </div>
                                  <div className="step-video-buttons">
                                    <button 
                                      type="button" 
                                      onClick={() => removeMethodStepImage(step.id)}
                                      className="btn-secondary"
                                    >
                                      Supprimer
                                    </button>
                                    <button 
                                      type="button" 
                                      onClick={() => handleStepVideoConfirm(step.id)}
                                      className="btn-primary"
                                    >
                                      Confirmer
                                    </button>
                                  </div>
                                </div>
                              )}

                              {step.imageStep === 'preview' && (step.croppedImage || (step.fileType === 'video' && step.imageSrc)) && (
                                <div className="step-image-preview">
                                  {step.fileType === 'video' ? (
                                    <VideoPlayer
                                      src={step.croppedImage?.url || step.imageSrc}
                                      autoPlay={false}
                                      muted={true}
                                      onDurationChange={(duration) => setVideoDuration(duration)}
                                    />
                                  ) : (
                                    <img 
                                      src={step.croppedImage.url} 
                                      alt={`Étape ${index + 1}`}
                                      className="step-preview-image"
                                    />
                                  )}
                                  <div className="step-image-actions">
                                    {!isEditMode && (
                                      <>
                                        <button 
                                          type="button" 
                                          onClick={() => handleStepBackToCrop(step.id)} 
                                          className="btn-secondary"
                                          title="Modifier l'image"
                                        >
                                          ✏️ Modifier
                                        </button>
                                        <button 
                                          type="button" 
                                          onClick={() => handleStepImageReset(step.id)} 
                                          className="btn-secondary"
                                          title="Supprimer l'image"
                                        >
                                          🗑️ Supprimer
                                        </button>
                                      </>
                                    )}
                                    {isEditMode && (
                                      <div className="edit-mode-notice-step">
                                        <span className="edit-mode-text">
                                          📷 Médias non modifiables en mode édition
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Description - style PostCard */}
                            <div className="step-description-section">
                              {step.showTextarea || step.text.trim() ? (
                                <div className={`step-description-display step-rich-text step-editor-${step.id}`}>
                                  <RichTextEditor
                                    value={step.text}
                                    onChange={(val) => updateMethodStep(step.id, val)}
                                    placeholder={`Décrivez l'étape ${index + 1}... Utilisez @nom et #hashtag`}
                                    maxLength={1000}
                                    className="step-textarea"
                                    onMentionSelect={(user) => handleMentions(user)}
                                  />
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Révéler l'éditeur riche et le focaliser
                                    updateMethodStepImage(step.id, { showTextarea: true });
                                    setTimeout(() => {
                                      document.querySelector(`.step-editor-${step.id} .editor-content`)?.focus();
                                    }, 100);
                                  }}
                                  className="add-description-btn"
                                >
                                  + Ajouter une description
                                </button>
                              )}
                            </div>

                            {/* Bouton supprimer l'étape */}
                            {methodSteps.length > 1 && (
                              <div className="step-delete-section">
                                <button
                                  type="button"
                                  onClick={() => removeMethodStep(step.id)}
                                  className="btn-delete-step"
                                  title={`Supprimer l'étape ${index + 1}`}
                                >
                                  🗑️ Supprimer cette étape
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                    
                        <button
                          type="button"
                          onClick={addMethodStep}
                          className="add-step-btn"
                        >
                      + Ajouter une étape
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Section ressources avec pattern step-description */}
                <div className="resources-section">
                  {showResourcesSection || ingredients.trim() ? (
                    <div className="resources-section-display">
                      <textarea
                        id="ingredients"
                        value={ingredients}
                        onChange={(e) => setIngredients(e.target.value)}
                        placeholder="Listez les matériaux, outils, logiciels... (un par ligne)"
                        rows="4"
                        className="ingredients-textarea"
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setShowResourcesSection(true);
                        setTimeout(() => {
                          document.getElementById('ingredients')?.focus();
                        }, 100);
                      }}
                      className="add-description-btn"
                    >
                      + Ajouter des ressources
                    </button>
                  )}
                </div>

                {/* Section localisation optionnelle */}
                <div className="location-section">
                  {!location || !isLocationValidated ? (
                    <button
                      type="button"
                      onClick={() => setShowLocationPopup(true)}
                      className="add-first-method-btn"
                    >
                      + Ajouter une localisation
                    </button>
                  ) : (
                    <div className="option-added">
                      <div className="option-summary">
                        <span className="option-label">📍 Localisation</span>
                        <p className="option-preview">
                          {location.address || `${location.lat?.toFixed(4)}, ${location.lng?.toFixed(4)}`}
                        </p>
                        <span className="option-meta">
                          {location.address ? 'Adresse validée' : 'Coordonnées GPS'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowLocationPopup(true)}
                        className="edit-option-btn"
                      >
                        ✏️ Modifier
                      </button>
                    </div>
                  )}
                </div>

                {/* Ancienne section bouton publier supprimée */}
              </div>
            )}
          </form>
        )}
      </div>

      {/* Section de progression d'upload fullscreen */}
      {isUploading && (
        <div className="upload-progress-fullscreen">
          <div className="upload-progress-content">
            <div className="upload-status-text">{uploadStatus}</div>
            <div className="upload-progress-bar">
              <div 
                className="upload-progress-fill" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <div className="upload-percentage">{Math.round(uploadProgress)}%</div>
          </div>
        </div>
      )}

      {/* Popup Localisation seulement */}

      {/* Popup Localisation */}
      {showLocationPopup && (
        <div className="popup-overlay" onClick={() => setShowLocationPopup(false)}>
          <div className="popup-content location-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>Localisation</h3>
              <button 
                className="close-popup-btn"
                onClick={() => setShowLocationPopup(false)}
              >
                ×
              </button>
            </div>
            <div className="popup-body">
              <LocationPicker
                onLocationSelect={(selectedLocation) => {
                  setLocation(selectedLocation);
                  setIsLocationValidated(true);
                  // legacy picker removed
                  // Ne pas fermer le popup automatiquement, laisser l'utilisateur cliquer sur "Valider"
                }}
                initialLocation={location}
                isVisible={true}
              />
            </div>
            <div className="popup-footer">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setLocation(null);
                  setIsLocationValidated(false);
                  setShowLocationPopup(false);
                }}
              >
                Supprimer localisation
              </button>
              <button 
                className="btn-primary"
                onClick={() => setShowLocationPopup(false)}
              >
                Valider
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreatePost;
